import type { TonalKpiTone } from '@/components/ui/generics/data-display/tonal-kpi-tile/TonalKpiTile';
import type { BadgeTone } from '@/components/ui/personalized/feedback/badge/Badge';
import type { AppointmentFinancialStatus } from '@/lib/api';

/**
 * Projeção financeira de um atendimento (deriva de status + financialStatus no
 * backend via AppointmentLifecycle.financialBucketOf). É o vocabulário que os
 * CARDS de resumo e o GRÁFICO "Fluxo diário" exibem.
 */
export type FinancialBucket =
  | 'forecast'
  | 'paid'
  | 'receivable'
  | 'undecided'
  | 'courtesy'
  | 'cancelled'
  | 'no_show';

export interface FinancialStatusColor {
  /** Cor do CARD (família de tom do DS: soft/border/valor via ds-stat--tonal-<tone>). */
  tone: TonalKpiTone;
  /** Token de cor base (css var) - usado pelo GRÁFICO (fill da barra + swatch). */
  token: string;
  /**
   * Tom do BADGE (FinancialStatusBadge) - só nos buckets que viram decisão de um
   * atendimento COMPLETED (paid/receivable/undecided/courtesy). Mesma cor do card
   * (success/warning/info=brand/admin=teal). forecast/cancelled/no_show não têm
   * badge financeiro (usam o StatusBadge operacional).
   */
  badgeTone?: BadgeTone;
}

/**
 * FONTE ÚNICA de COR por status financeiro. Cards e gráfico consomem daqui -
 * antes a cor vivia em 3 mapas hardcoded (tons inline dos cards, fills inline +
 * swatch CSS do gráfico). Mudar a cor de um status = editar UMA entrada aqui
 * (reflete nas duas superfícies, no mesmo ponto).
 *
 * Escopo = COR (tone+token), não label: os rótulos diferem por contexto e por
 * isso ficam em cada superfície (card fala de R$ "a receber"; gráfico de volume
 * "a concluir") - unificar texto seria decisão de produto, não de refactor.
 *
 * `tone` e `token` representam a MESMA cor por status (o tom do card resolve no
 * mesmo hue do token do gráfico). Cores atuais preservadas (refactor sem mudança
 * visual); a separação Previsto≠Decisão é feita aqui numa etapa seguinte.
 */
export const FINANCIAL_STATUS_COLOR: Record<FinancialBucket, FinancialStatusColor> = {
  forecast: { tone: 'purple', token: '--accent-purple' },
  paid: { tone: 'success', token: '--success', badgeTone: 'success' },
  receivable: { tone: 'warning', token: '--warning', badgeTone: 'warning' },
  undecided: { tone: 'brand', token: '--brand-500', badgeTone: 'info' },
  courtesy: { tone: 'admin', token: '--admin-500', badgeTone: 'admin' },
  cancelled: { tone: 'error', token: '--error' },
  no_show: { tone: 'pink', token: '--accent-pink' },
};

/** Decisão financeira (enum de um COMPLETED) -> bucket do mapa. */
const FINANCIAL_STATUS_BUCKET: Record<AppointmentFinancialStatus, FinancialBucket> = {
  PAID: 'paid',
  RECEIVABLE: 'receivable',
  UNDECIDED: 'undecided',
  COURTESY: 'courtesy',
};

/**
 * Tom do badge financeiro a partir da decisão (FinancialStatusBadge + history).
 * Fonte única = FINANCIAL_STATUS_COLOR (mesma cor do card/gráfico/legenda) -
 * substitui o antigo FINANCIAL_STATUS_TONES hardcoded.
 */
export function financialBadgeTone(status: AppointmentFinancialStatus): BadgeTone {
  return FINANCIAL_STATUS_COLOR[FINANCIAL_STATUS_BUCKET[status]].badgeTone ?? 'neutral';
}
